import { X } from "lucide-react";
import type { ReactNode } from "react";

export interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  headerExtra?: ReactNode;
  maxWidth?: string;
}

/**
 * Standard modal shell: header (title + close icon) and footer (actions, if
 * any) stay fixed; only the body between them scrolls, so max-height is
 * always the viewport minus header/footer/spacing rather than the whole
 * panel scrolling as one block.
 */
export function Modal({ title, onClose, children, footer, headerExtra, maxWidth = "max-w-lg" }: ModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={`glass relative z-10 flex max-h-[85vh] w-full ${maxWidth} flex-col rounded-[var(--radius-lg)] shadow-[var(--shadow-glass)]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--glass-border)] px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-display text-lg font-semibold text-[var(--text-primary)]">
              {title}
            </h2>
            {headerExtra}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--text-muted)] transition-colors hover:bg-white/10 hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">{children}</div>

        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-[var(--glass-border)] px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
